'use client';

import React, {useState, useEffect} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Autoplay, Navigation, Pagination} from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import ImageRaw from '../../../../components/image-raw';

interface HiglightSwiperProps {
    highlightMoments: string[];
    renderHighlightMoment: (imageUrl: string) => React.ReactNode;
    autoPlay?: boolean;
    autoPlayInterval?: number;
    loop?: boolean;
}

const HiglightSwiper: React.FC<HiglightSwiperProps> = ({
    highlightMoments,
    renderHighlightMoment,
    autoPlay = true,
    autoPlayInterval = 3000,
    loop = true,
}) => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Mobile component - sử dụng Swiper với touch support
    if (isMobile) {
        return (
            <div className="relative w-full">
                <Swiper
                    modules={[Autoplay]}
                    loop={loop}
                    autoplay={
                        autoPlay
                            ? {
                                  delay: autoPlayInterval,
                                  disableOnInteraction: false,
                              }
                            : false
                    }
                    slidesPerView={1.5}
                    centeredSlides={true}
                    spaceBetween={10}
                    grabCursor={true}
                    allowTouchMove={true}
                    className="highlight-swiper-mobile">
                    {highlightMoments.map(item => (
                        <SwiperSlide key={item} className="highlight-slide-mobile">
                            {renderHighlightMoment(item)}
                        </SwiperSlide>
                    ))}
                </Swiper>

                <style>{`
            .highlight-swiper-mobile {
              width: 100%;
              height: auto;
            }
            
            .highlight-slide-mobile {
              display: flex;
              justify-content: center;
              align-items: center;
            }
            
            .highlight-swiper-mobile .swiper-slide {
              opacity: 0.6;
              transform: scale(0.75);
            }
            
            .highlight-swiper-mobile .swiper-slide-active {
              opacity: 1;
              transform: scale(1);
            }
            
            .highlight-swiper-mobile .swiper-slide-prev,
            .highlight-swiper-mobile .swiper-slide-next {
              opacity: 0.6;
              transform: scale(0.75);
            }
          `}</style>
            </div>
        );
    }

    // Desktop component - dùng Swiper bình thường
    return (
        <div className="relative w-full">
            <Swiper
                modules={[Autoplay]}
                loop={loop}
                autoplay={
                    autoPlay
                        ? {
                              delay: autoPlayInterval,
                              disableOnInteraction: false,
                              pauseOnMouseEnter: true,
                          }
                        : false
                }
                grabCursor={true}
                slidesPerView={'auto'}
                centeredSlides={true}
                spaceBetween={20}
                className="highlight-swiper-desktop w-full">
                {highlightMoments.map(item => (
                    <SwiperSlide key={item} className="highlight-slide-desktop">
                        {renderHighlightMoment(item)}
                    </SwiperSlide>
                ))}
            </Swiper>

            <style>{`
          .highlight-swiper-desktop {
            width: 100%;
            height: auto;
            padding: 0 !important;
          }

          .highlight-slide-desktop {
            display: flex;
            justify-content: center;
            align-items: center;
          }

          .highlight-swiper-desktop .swiper-slide {
            opacity: 0.6;
            transform: scale(0.75);
          }
            
          .highlight-swiper-desktop .swiper-slide-active {
            opacity: 1;
            transform: scale(1);
          }
          
          .highlight-swiper-desktop .swiper-slide-prev,
          .highlight-swiper-desktop .swiper-slide-next {
            opacity: 0.6;
            transform: scale(0.75);
          }

          @media (min-width: 768px) {
            .highlight-slide-desktop {
              width: 362px !important;
            }
          }
          
          @media (min-width: 1280px) {
            .highlight-slide-desktop {
              width: 788px !important;
            }
          }
        `}</style>
        </div>
    );
};

export default HiglightSwiper;
